import { useMemo } from "react";

import { Control, useFormContext } from "react-hook-form";

import { Box } from "@mui/material";

import FormInsuranceCarrierAutocomplete from "@covertree/ui/forms/FormInsuranceCarrierAutocomplete";

import { PriorInsurance } from "@covertree/new-business-graphql";
import { FormSelectableWithValue } from "@covertree/types";
import { FormRadioGroup } from "@covertree/ui";

import { EffectiveDateExposureAndLapseForm } from "../validatorsAndTypes/effectiveDateExposureAndLapseForm";

import { PurchaseDateSelect } from "./PurchaseDateSelect";
import { InsuranceExpirationSelect } from "./InsuranceExpirationSelect";
import {
  useNewBusinessSelector,
  selectIsBookConversion,
} from "@covertree/new-business-redux";
const getInsuranceStatusTitle = (arg: PriorInsurance, isD2C: boolean): string =>
  ({
    [PriorInsurance.SwitchingCarriers]: `Yes, ${isD2C ? "I am" : "Applicant is"} thinking about switching carriers`,
    [PriorInsurance.BuyingNewHome]: `No, ${isD2C ? "I am" : "Applicant is"} buying a new or pre-owned home`,
    [PriorInsurance.RentingNewHome]: `No, ${isD2C ? "I" : "Applicant"} just started renting the home`,
    [PriorInsurance.NoInsurance]: `No, ${isD2C ? "I don't" : "Applicant doesn't"} have insurance today`,
  })[arg]!;

const getInsuranceStatusItems = ({
  control,
  priorInsuranceStatusValue,
  isRRFlow,
  isD2C,
  isBookConversion,
}: {
  control: Control<EffectiveDateExposureAndLapseForm>;
  isRRFlow: boolean;
  isD2C: boolean;
  priorInsuranceStatusValue?: PriorInsurance;
  isBookConversion?: boolean;
}): InsuranceStatus[] => {
  const statuses: InsuranceStatus[] = [
    {
      image: "/images/card-icons/refresh.webp",
      imageAlt: "Applicant has an insurance",
      title: isBookConversion
        ? "Yes, this is a book conversion quote"
        : getInsuranceStatusTitle(PriorInsurance.SwitchingCarriers, isD2C),
      value: PriorInsurance.SwitchingCarriers,
      additionalBodyContentDirection: "column",
      additionalBodyContent:
        !isD2C &&
        (priorInsuranceStatusValue === PriorInsurance.SwitchingCarriers ||
          isBookConversion) ? (
          <Box>
            <FormInsuranceCarrierAutocomplete
              control={control}
              name="priorInsuranceCarrierName"
              label="Applicant's current insurer"
            />
          </Box>
        ) : undefined,
    },
  ];

  if (isBookConversion) return statuses;

  if (isRRFlow) {
    statuses.push({
      image: "/images/card-icons/new-home.webp",
      imageAlt: "Just rented home",
      title: getInsuranceStatusTitle(PriorInsurance.RentingNewHome, isD2C),
      value: PriorInsurance.RentingNewHome,
      additionalBodyContent: undefined,
    });
  } else {
    statuses.push({
      image: "/images/card-icons/new-home.webp",
      imageAlt: "Just bought home",
      title: getInsuranceStatusTitle(PriorInsurance.BuyingNewHome, isD2C),
      value: PriorInsurance.BuyingNewHome,
      additionalBodyContentDirection: "column",
      additionalBodyContent:
        !isD2C && priorInsuranceStatusValue === PriorInsurance.BuyingNewHome ? (
          <Box>
            <PurchaseDateSelect control={control} isD2C={isD2C} />
          </Box>
        ) : undefined,
    });
  }

  return [
    ...statuses,
    {
      image: "/images/card-icons/no-insurance.webp",
      imageAlt: "I don't have an insurance",
      title: getInsuranceStatusTitle(PriorInsurance.NoInsurance, isD2C),
      value: PriorInsurance.NoInsurance,
      additionalBodyContentDirection: "column",
      additionalBodyContent:
        priorInsuranceStatusValue === PriorInsurance.NoInsurance &&
        !isRRFlow &&
        !isD2C ? (
          <Box>
            <InsuranceExpirationSelect
              control={control}
              label="When did applicant's insurance expire?"
            />
          </Box>
        ) : undefined,
    },
  ];
};

type InsuranceStatus = FormSelectableWithValue<PriorInsurance>;

interface InsuranceStatusRadioGroupProps {
  isD2C: boolean;
  isRRFlow: boolean;
  onOptionSelect?: (option: unknown) => void;
}

export function PriorInsuranceStatusRadioGroup({
  isD2C,
  isRRFlow,
  onOptionSelect,
}: InsuranceStatusRadioGroupProps) {
  const { control, watch } =
    useFormContext<EffectiveDateExposureAndLapseForm>();

  const isBookConversion = useNewBusinessSelector(selectIsBookConversion);
  const priorInsuranceStatusValue = watch("priorInsuranceSituationStatus");

  const insuranceStatuses = useMemo<InsuranceStatus[]>(
    () =>
      getInsuranceStatusItems({
        control,
        priorInsuranceStatusValue,
        isRRFlow,
        isD2C,
        isBookConversion,
      }),
    [control, priorInsuranceStatusValue, isRRFlow, isD2C]
  );

  return (
    <FormRadioGroup
      name="priorInsuranceSituationStatus"
      control={control}
      selected={priorInsuranceStatusValue}
      direction="column"
      label={
        isD2C ? "" : "Does the applicant already have homeowners insurance?"
      }
      items={insuranceStatuses}
      onOptionSelect={onOptionSelect}
    />
  );
}
